import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GitMerge, Search, Calculator, FileSearch, FileSignature, Users, ArrowRight } from 'lucide-react';
import SEO, { generateServiceSchema } from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const MergersAcquisitionsPage = () => {
  const { locale } = useParams();

  const stages = [
    { step: '01', title: 'Strategic Assessment', description: 'M&A readiness, acquisition thesis, target screening', icon: Search },
    { step: '02', title: 'Deal Origination', description: 'Proprietary target identification and approach', icon: GitMerge },
    { step: '03', title: 'Valuation & Financial Modelling', description: 'DCF, comparable analysis, synergy modelling', icon: Calculator },
    { step: '04', title: 'Due Diligence', description: 'Commercial, financial, legal, and technical diligence', icon: FileSearch },
    { step: '05', title: 'Deal Structuring & Negotiation', description: 'Term sheet, SPA negotiation, regulatory approvals', icon: FileSignature },
    { step: '06', title: 'Post-Merger Integration', description: '100-day plans, operational integration, cultural alignment', icon: Users },
  ];

  const sectors = ['Pharmaceuticals', 'Industrial Manufacturing', 'Capital Markets', 'Healthcare', 'Technology'];

  const serviceSchema = generateServiceSchema({
    type: 'M&A Advisory',
    name: 'Mergers & Acquisitions Advisory',
    description: 'Full transaction lifecycle advisory from deal origination to post-merger integration.'
  });

  return (
    <div className="pt-20" data-testid="mergers-acquisitions-page">
      <SEO 
        title="Mergers & Acquisitions Advisory"
        description="Complete M&A transaction lifecycle advisory including strategic assessment, deal origination, valuation, due diligence, and post-merger integration."
        path="/services/mergers-acquisitions"
        structuredData={serviceSchema}
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-teal/5" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <span className="inline-flex items-center px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-medium mb-6">
                <GitMerge className="w-4 h-4 mr-2" />
                Transaction Advisory
              </span>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy leading-tight mb-6">
                Mergers & Acquisitions Advisory
              </h1>
              <p className="text-lg text-mid-grey leading-relaxed mb-8">
                Full transaction lifecycle advisory — from identifying strategic targets to closing deals and managing integration. Financial rigor meets industry-specific diligence.
              </p>
              <Link to={`/${locale}/contact`} className="btn-primary">
                Start an M&A Discussion
              </Link>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block">
              <img 
                src="/images/external/photo-1560472355-536de3962603.jpg"
                alt="Business handshake"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6-Stage Process */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Our Process</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">6-Stage M&A Process</motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stages.map((stage, index) => {
              const IconComponent = stage.icon;
              return (
                <motion.div
                  key={stage.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-light-grey p-6 rounded-xl relative"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal to-navy text-white rounded-xl flex items-center justify-center font-bebas text-xl flex-shrink-0">
                      {stage.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy mb-2">{stage.title}</h4>
                      <p className="text-mid-grey text-sm">{stage.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-center font-playfair text-2xl text-navy mb-8">Sectors We Cover</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {sectors.map((sector) => (
              <span key={sector} className="px-6 py-3 bg-white rounded-full text-navy font-medium shadow-sm">
                {sector}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">Start an M&A Discussion</h2>
          <p className="text-white/80 text-lg mb-8">Whether buying, selling, or merging — our advisors guide you through every step.</p>
          <Link to={`/${locale}/contact`} className="btn-primary inline-flex items-center">
            Schedule Consultation <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MergersAcquisitionsPage;
